import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import {
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositoryEntries,
	selectEntryReadableValues,
} from '../containers/contentSlice';
import { useGetRepositoryByIdQuery, useGetRepositoryEntriesByFilterQuery } from '../services/repositoryEntries';
import { populateRepositoryEntry } from '../common/util';
import {
	BelongsToPopulatedValue,
	EntityType,
	EntryProperty,
	EntryPropertyBelongsTo,
	PopulatedEntry,
	PropertyDefinitionBelongsTo,
	PropertyDefinitionHas,
	RepositoryEntry,
} from '../types';
import type { RelationValue } from '../components/EntryPropertyBelongsToEditDialog';

export type RelationOption = { id: string; label: string };

/**
 * Selectable target entries for a relation property, plus the selection rules shared by every
 * relation editor (required, single vs. many, archived targets, `has` targets already linked elsewhere).
 */
export function useRelationOptions({
	def,
	values,
	selectedEntryIds,
	enabled,
}: {
	def: PropertyDefinitionBelongsTo | PropertyDefinitionHas;
	/** Currently stored values; their labels back up entries missing from the loaded options. */
	values: RelationValue[];
	selectedEntryIds: string[];
	enabled: boolean;
}) {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const entryReadableValues = useAppSelector(selectEntryReadableValues);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);

	const targetRepositoryId = def.targetRepositoryId;
	const linkedEntryIds = useMemo(
		() => Array.from(new Set(values.map((v) => String(v.value || '').trim()).filter(Boolean))),
		[values],
	);
	const { data: targetRepositoryByIdResponse, isFetching: isTargetRepositoryByIdFetching } = useGetRepositoryByIdQuery(targetRepositoryId, {
		skip: !enabled || !isAuthenticated || !targetRepositoryId,
	});
	const { data: targetRepositoryActiveEntriesResponse, isFetching: isTargetRepositoryActiveEntriesFetching } = useGetRepositoryEntriesByFilterQuery({
		repositoryId: targetRepositoryId ?? '',
		includeArchived: false,
	}, {
		skip: !enabled || !isAuthenticated || !targetRepositoryId,
	});
	const linkedIdsFilter = useMemo(
		() => (linkedEntryIds.length ? JSON.stringify({ id: { in: linkedEntryIds } }) : undefined),
		[linkedEntryIds],
	);
	const { data: targetRepositoryLinkedEntriesResponse, isFetching: isTargetRepositoryLinkedEntriesFetching } = useGetRepositoryEntriesByFilterQuery({
		repositoryId: targetRepositoryId ?? '',
		includeArchived: true,
		dataFilter: linkedIdsFilter,
	}, {
		skip: !enabled || !isAuthenticated || !targetRepositoryId || linkedEntryIds.length === 0,
	});
	const targetRepositoryEntriesResponse = useMemo(() => {
		const activeRows = targetRepositoryActiveEntriesResponse?.rows ?? [];
		const linkedRows = targetRepositoryLinkedEntriesResponse?.rows ?? [];
		const rowsById = new Map(activeRows.map((row) => [row.id, row]));
		for (const row of linkedRows) {
			if (!rowsById.has(row.id)) {
				rowsById.set(row.id, row);
			}
		}
		return { rows: Array.from(rowsById.values()) };
	}, [targetRepositoryActiveEntriesResponse?.rows, targetRepositoryLinkedEntriesResponse?.rows]);
	const isLoading = isAuthenticated && (
		isTargetRepositoryByIdFetching || isTargetRepositoryActiveEntriesFetching || isTargetRepositoryLinkedEntriesFetching
	);

	const targetRepositoryPropertyDefinitions = useMemo(() => {
		if (isAuthenticated) {
			return targetRepositoryByIdResponse?.propertyDefinitions ?? [];
		}
		return allPropertyDefinitions.filter((def) => def.parentId === targetRepositoryId);
	}, [allPropertyDefinitions, targetRepositoryId, isAuthenticated, targetRepositoryByIdResponse?.propertyDefinitions]);

	const targetRepositoryEntries = useMemo(() => {
		if (isAuthenticated) {
			return targetRepositoryEntriesResponse?.rows ?? [];
		}
		const currentRepositoryEntries = allRepositoryEntries.filter(
			(entry) => entry.repositoryId === targetRepositoryId,
		) as RepositoryEntry[];
		const entryProperties = allEntryProperties.filter(
			(entry) =>
				entry.repositoryId === targetRepositoryId,
		) as EntryProperty[];
		const entryHasProperties = allEntryProperties.filter(
			(entry) =>
				entry.type === EntityType.EntryPropertyBelongsTo &&
				(entry as EntryPropertyBelongsTo).targetRepositoryId === targetRepositoryId,
		) as EntryPropertyBelongsTo[];
		return currentRepositoryEntries.map((repositoryEntry) =>
			populateRepositoryEntry(
				repositoryEntry,
				entryProperties,
				entryHasProperties,
				targetRepositoryPropertyDefinitions,
				entryReadableValues,
			),
		);
	}, [targetRepositoryEntriesResponse?.rows, isAuthenticated, allRepositoryEntries, allEntryProperties, targetRepositoryId, targetRepositoryPropertyDefinitions, entryReadableValues]);

	const targetRepositoryBelongsToProp =
		def.type === EntityType.PropertyDefinitionHas
			? (targetRepositoryPropertyDefinitions.find(
				(pd) =>
					pd.type === EntityType.PropertyDefinitionBelongsTo &&
						pd.slug === (def as PropertyDefinitionHas).targetPropertySlug,
			) as PropertyDefinitionBelongsTo | undefined)
			: undefined;
	const canTargetEntriesHaveMultipleValues = targetRepositoryBelongsToProp?.allowMany ?? false;

	const disableSelect = def.type === EntityType.PropertyDefinitionHas && !canTargetEntriesHaveMultipleValues;
	const isRequiredBelongsTo =
		def.type === EntityType.PropertyDefinitionBelongsTo &&
		!!(def as PropertyDefinitionBelongsTo).required;
	const allowMany = def.type !== EntityType.PropertyDefinitionBelongsTo || !!(def as PropertyDefinitionBelongsTo).allowMany;

	const apiLabelByEntryId = useMemo(() => {
		const m = new Map<string, string>();
		for (const e of targetRepositoryEntriesResponse.rows) {
			m.set(e.id, e.label ?? '');
		}
		return m;
	}, [targetRepositoryEntriesResponse]);

	const archivedByEntryId = useMemo(() => {
		const m = new Map<string, boolean>();
		if (isAuthenticated) {
			for (const e of targetRepositoryEntriesResponse.rows) {
				m.set(e.id, e.isArchived);
			}
		} else {
			for (const entry of targetRepositoryEntries as PopulatedEntry[]) {
				m.set(entry.id as string, entry.isArchived);
			}
		}
		return m;
	}, [isAuthenticated, targetRepositoryEntriesResponse.rows, targetRepositoryEntries]);

	const isEntryArchived = useCallback(
		(entryId: string) => archivedByEntryId.get(entryId) ?? false,
		[archivedByEntryId],
	);

	const canDeselectEntry = useCallback(
		(entryId: string) => {
			if (disableSelect) {
				return false;
			}
			if (!isRequiredBelongsTo) {
				return true;
			}
			if (isEntryArchived(entryId)) {
				return false;
			}
			if (allowMany) {
				return selectedEntryIds.length > 1;
			}
			return false;
		},
		[disableSelect, isRequiredBelongsTo, allowMany, selectedEntryIds, isEntryArchived],
	);

	const options = useMemo((): RelationOption[] => {
		if (isAuthenticated) {
			return targetRepositoryEntriesResponse.rows
				.filter((e) => {
					const targetPropertyValue = (e as PopulatedEntry).data[targetRepositoryBelongsToProp?.slug ?? ''] as BelongsToPopulatedValue[] | undefined;
					return (!e.isArchived || selectedEntryIds.includes(e.id)) && (canTargetEntriesHaveMultipleValues || !targetPropertyValue?.length || selectedEntryIds.includes(e.id));
				})
				.map((e) => ({
					id: e.id,
					label: (e.label ?? '').trim() || '(no label)',
				}));
		}
		return (targetRepositoryEntries as PopulatedEntry[])
			.filter((entry) => {
				const targetPropertyValue = entry.data[targetRepositoryBelongsToProp?.slug ?? ''] as BelongsToPopulatedValue[] | undefined;
				return (!entry.isArchived || selectedEntryIds.includes(entry.id as string)) &&
					(canTargetEntriesHaveMultipleValues || !targetPropertyValue?.length || selectedEntryIds.includes(entry.id as string));
			})
			.map((entry) => ({
				id: entry.id as string,
				label: entryReadableValues.get(entry.id as string) ?? '(no label)',
			}));
	}, [
		isAuthenticated,
		targetRepositoryEntriesResponse,
		entryReadableValues,
		targetRepositoryBelongsToProp,
		canTargetEntriesHaveMultipleValues,
		selectedEntryIds,
		targetRepositoryEntries,
	]);

	const labelForEntryId = useCallback(
		(entryId: string) => {
			if (isAuthenticated) {
				return (
					apiLabelByEntryId.get(entryId) ??
					values.find((v) => v.value === entryId)?.label ??
					'(no label)'
				);
			}
			return entryReadableValues.get(entryId) ?? '(no label)';
		},
		[isAuthenticated, apiLabelByEntryId, values, entryReadableValues],
	);

	return {
		options,
		isLoading,
		allowMany,
		disableSelect,
		isEntryArchived,
		canDeselectEntry,
		labelForEntryId,
	};
}
